import { isArray, isObject, omit, isNil } from "lodash"
import { ASSET_TYPES } from "helpers/assets/constants"

export const CUMULATIVE_RETURNS_SUBQUERY = `
  cumulativePercent
  cumulativeCurrency
`

export const GROUP_BY_TYPES = {
  CLASSIFICATION: "CLASSIFICATION",
  TAG: "TAG",
  POSITION_ATTRIBUTE: "POSITION_ATTRIBUTE",
}

export function hasIdioWeightingRule(positionSet) {
  if (positionSet?.type !== "RULES") {
    return false
  }

  const rules = positionSet?.rulesConfiguration?.rules
  if (!rules) {
    return false
  }

  return rules.some(rule => rule?.weighting?.idioWeighting === true)
}
/**
 * Creates a tooltip getter function that references another metric's value.
 * Useful for creating relationships between metrics (e.g., showing residual in total return tooltip).
 *
 * @param {Object} referencedMetric - The metric to get the value from (must have valueGetter and formatter)
 * @param {Function} tooltipTemplate - Function that takes formatted value and returns tooltip text
 * @returns {Function} A tooltip getter function that can be used in metric definitions
 */
export function createMetricTooltip(referencedMetric, tooltipTemplate) {
  return (data) => {
    const value = referencedMetric.valueGetter(data)
    if (value === null || value === undefined) return null
    const formatted = referencedMetric.formatter(value)
    return tooltipTemplate(formatted)
  }
}

export function getGroupByOptions(groupByOptions) {
  // Handle null/undefined case
  if (!groupByOptions) {
    return { groupBy: null, groupById: null, tier: null }
  }

  if (!isObject(groupByOptions)) {
    return { groupBy: groupByOptions, groupById: null, tier: null }
  }

  // Handle classification group
  if (groupByOptions.isClassification) {
    const classificationId = groupByOptions.classificationId || groupByOptions.id
    return {
      groupBy: GROUP_BY_TYPES.CLASSIFICATION,
      groupById: classificationId,
      tier: groupByOptions.id,
    }
  }

  // Handle tag group
  if (groupByOptions.isTag) {
    return {
      groupBy: GROUP_BY_TYPES.TAG,
      groupById: groupByOptions.id,
      tier: null,
    }
  }

  // Handle position attribute group
  if (groupByOptions.isPositionAttribute) {
    return {
      groupBy: GROUP_BY_TYPES.POSITION_ATTRIBUTE,
      groupById: groupByOptions.id,
      tier: null,
    }
  }

  // Handle custom group by type
  if (groupByOptions.id) {
    return {
      groupBy: groupByOptions.id,
      groupById: null,
      tier: null,
    }
  }
}

export function getGroupByFields(groupByOptions) {
  const { groupBy, groupById, tier } = getGroupByOptions(groupByOptions)
  return {
    groupBy,
    ...(groupById && { groupById }),
    ...(tier && { tier }),
  }
}

export function getGroupByQueryParameters({ groupBy, groupById, tier } = {}) {
  const parameters = []

  if (groupBy) {
    parameters.push("$groupBy: ContributorGroupType!")
  }
  if (groupById) {
    parameters.push("$groupById: String!")
  }
  if (tier) {
    parameters.push("$tier: String!")
  }

  return parameters.length ? `, ${parameters.join(", ")}` : ""
}

export function getGroupBySubqueryParameters({ groupBy, groupById, tier } = {}) {
  const parameters = []

  if (groupBy) {
    parameters.push("groupBy: $groupBy")
  }
  if (groupById) {
    parameters.push("groupById: $groupById")
  }
  if (tier) {
    parameters.push("classificationTier: $tier")
  }

  return parameters.join(", ")
}

export function sanitizePositionSet(positionSet) {
  const positionSetWithoutName = omit(positionSet, "name")
  if (!positionSet?.id && !isArray(positionSet?.dates)) {
    if (positionSet?.type === "RULES" && positionSet?.rulesConfiguration) {
      return positionSetWithoutName
    }
    return {}
  }

  if (positionSet.id) {
    return positionSetWithoutName
  }

  // Inline position sets cannot have attributes or name
  const assetTypeEntries = ASSET_TYPES.map(assetType => [assetType, null])
  const emptyPositions = Object.fromEntries(assetTypeEntries)

  const sanitizedDates = positionSet.dates.map(date => ({
    ...date,
    ...ASSET_TYPES.reduce((acc, assetType) => ({
      ...acc,
      [assetType]: date[assetType]?.map(position => omit(position, "attributes")) || [],
    }), emptyPositions),
  }))

  return {
    ...positionSetWithoutName,
    dates: sanitizedDates,
  }
}

export function getBetaInstrumentFromOptions(options) {
  if (!options.betaInstrument || options.isIntraday) {
    return null
  }

  return {
    id: options.betaInstrument.id,
    type: options.betaInstrument.type,
  }
}

export function getIsNoLongerHeldFromContributor(contributor) {
  if (contributor.notHeld) {
    return false
  }
  if (!isNil(contributor.positionSetEconomicExposure)) {
    return false
  }
  return isNil(contributor.values?.composition?.economicExposure)
}

export function makeUniverseFilter(positionSet, base) {
  const sanitizedPositionSet = getSourcePositionSet(sanitizePositionSet(positionSet))
  if (!sanitizedPositionSet.id) {
    return null
  }
  const universe = [{
    type: sanitizedPositionSet.type,
    in: sanitizedPositionSet.id,
  }]

  if (base && base.type !== "BENCHMARK") {
    universe.push({
      type: base.type === "BASKET" ? "SWAP" : base.type,
      in: base.id,
    })
  }

  return [{ universe }]
}

export function getSourcePositionSet(positionSet) {
  if (!positionSet) {
    return {}
  }
  if (positionSet.type !== "RULES") {
    return positionSet
  }
  if (positionSet.rulesConfiguration.source.positionSet) {
    return positionSet.rulesConfiguration.source.positionSet
  }
  return positionSet
}
